export const MEMBER_TYPES = [
  { slug: "pilots-flight-crew", label: "Pilots & Flight Crew", kicker: "Flight Operations", desc: "Commercial, corporate, and charter pilots plus cabin crew." },
  { slug: "charter-operators", label: "Charter Operators", kicker: "Air Charter", desc: "Part 135 operators and charter management companies." },
  { slug: "fbo-managers", label: "FBO Managers", kicker: "Ground Services", desc: "Fixed-base operators and ground handling professionals." },
  { slug: "aircraft-brokers", label: "Aircraft Brokers", kicker: "Transactions", desc: "Sales, acquisitions, and aircraft transaction specialists." },
  { slug: "parts-manufacturers", label: "Parts & Manufacturers", kicker: "Supply Chain", desc: "OEMs, MROs, and aviation parts suppliers." },
  { slug: "aviation-sales-service", label: "Aviation Sales & Service", kicker: "Technical Services", desc: "Avionics, maintenance, and aviation service providers." },
];

export const AEROINTEL_DOMAINS = [
  { slug: "aircraft", label: "Aircraft Intelligence", points: ["Aircraft systems", "Performance", "Limitations", "Operating procedures", "Comparisons"] },
  { slug: "faa", label: "FAA Intelligence", points: ["Regulations", "Advisory Circulars", "FAR interpretation", "Certification", "Operational guidance"] },
  { slug: "airport", label: "Airport Intelligence", points: ["Airport reviews", "FBO recommendations", "Hotels & restaurants", "Fuel", "Customs & crew transport"] },
  { slug: "career", label: "Career Intelligence", points: ["Pilot hiring", "Contract opportunities", "Salary insights", "Resume reviews", "Training"] },
  { slug: "maintenance", label: "Maintenance Intelligence", points: ["Maintenance planning", "Common failures", "Service Bulletins", "Parts", "AOG resources"] },
  { slug: "industry", label: "Industry Intelligence", points: ["Market trends", "Aircraft values", "Charter demand", "New aircraft", "Manufacturers"] },
];

export const MEMBERSHIP_TIERS = [
  // 2026-02 freemium reset — one crew tier. Role is a profile field, not a
  // billing tier. Kept alongside Operator/Client tiers for the /membership grid.
  { slug: "pro", lookup_key: "pjp_pro_yearly", label: "PJP Pro", kicker: "All Crew", price: 129, billing: "yearly", featured: true, tag: "One membership. Every professional tool. Every crew role.", perks: [
    "Full member profile with FAA verification (or manual for cabin crew)",
    "Access to the entire PJP crew directory — 26,511+ verified aviators",
    "Direct messaging with pilots, operators, and dispatchers",
    "Job Board — apply, save, and get alerts for the roles you want",
    "AeroIntel — unlimited AI-powered aviation knowledge base",
    "FREE JumpSeat Program access — fly empty legs ($10K/yr rider value)",
    "Fleet Live Map with historical flight trails",
    "Salary Insights + Community Docs downloads",
    "PJP Wallet · Digital Payments — trips collected upfront, paid guaranteed",
    "Unlimited Hot Mic posts, DMs, and comments",
  ]},
  { slug: "charter-operator", lookup_key: "pjp_operator_monthly", label: "Charter Operator", kicker: "Operations", price: 299, tag: "For Part 135 operators, charter companies, and flight departments.", perks: [
    "Verified company profile with fleet listings",
    "Add unlimited aircraft — tail number, type, range, seats",
    "Send roster invites directly to PJP pilots",
    "Access to the full PJP member directory",
    "Featured placement in the Charter Operators directory",
    "Connect with 26,511+ aviation professionals",
    "Job board & contract opportunity postings",
    "PJP Wallet · Digital Payments — collect trips upfront, pay crew guaranteed",
  ]},
  { slug: "charter-client", lookup_key: "pjp_charter_client_yearly", label: "Charter Client", kicker: "Fly Private · Members Only", price: 5000, billing: "yearly", tag: "For companies and individuals who charter private jets. Access PJP's operator-direct RFQ network for institutional-grade pricing.", perks: [
    "Broadcast every trip to every PJP operator flying your requested type",
    "Receive live bids in your inbox — no more single-vendor lock-in",
    "PJP-negotiated volume pricing on top of operator bids",
    "$250 flat booking fee if PJP handles logistics (contract, deposit, catering, ground)",
    "Or deal direct with the operator at zero markup — you choose",
    "Priority JumpSeat alerts before they hit the public marketplace",
    "Dedicated concierge for AOG, catering, ground, customs",
  ]},
];

// Crew roles — the profile-level classification. Selected during signup.
// Drives (a) verification path (FAA registry for pilot/mechanic, manual
// upload for flight attendant), (b) role badge on profiles, (c) role-aware
// Hot Mic accent colors.
export const CREW_ROLES = [
  { slug: "pilot",            label: "Pilot",             kicker: "Flight Deck",   desc: "Commercial, corporate, or charter pilots holding an FAA airman certificate.",   icon: "Plane",       verification: "faa" },
  { slug: "flight-attendant", label: "Flight Attendant",  kicker: "Cabin Crew",    desc: "Corporate or Part 135 cabin crew. Verified via employer documentation.",         icon: "Users",       verification: "manual" },
  { slug: "mechanic",         label: "Mechanic · AMT",    kicker: "Maintenance",   desc: "A&P mechanics, IAs, and avionics technicians. FAA registry verified.",           icon: "Wrench",      verification: "faa" },
  { slug: "dispatcher",       label: "Dispatcher",        kicker: "Ops · Ground",  desc: "Flight dispatchers, schedulers, ops coordinators, and ground staff.",             icon: "Radio",       verification: "manual" },
  { slug: "avexec",           label: "AV/EXEC",           kicker: "Industry",      desc: "Aviation executives, brokers, MRO managers, and other industry professionals.", icon: "Briefcase",   verification: "manual" },
];

// Legacy PJP_PRO_LOOKUP_KEY export for other files that reference it.
export const PJP_PRO_LOOKUP_KEY = "pjp_pro_yearly";
export const PJP_PRO_PRICE = 129;

// Charter-client booking fee (when PJP handles the booking end-to-end).
export const CHARTER_CLIENT_BOOKING_FEE = 250;
export const CHARTER_CLIENT_ANNUAL_PRICE = 5000;
export const CHARTER_CLIENT_LOOKUP_KEY = "pjp_charter_client_yearly";

export const DIGITAL_ID_PRICE = 199;
export const DIGITAL_ID_LOOKUP_KEY = "pjp_digital_id_once";

// Aircraft types (short list; full list can be added later)
export const AIRCRAFT_TYPES = [
  "Any Aircraft", "Cessna Citation CJ4", "Cessna Citation Latitude", "Cessna Citation Longitude", "Cessna Citation X",
  "Embraer Phenom 300E", "Embraer Praetor 600", "Embraer Legacy 500", "Bombardier Challenger 350", "Bombardier Challenger 650",
  "Bombardier Global 5500", "Bombardier Global 7500", "Gulfstream G280", "Gulfstream G450", "Gulfstream G550",
  "Gulfstream G650", "Gulfstream G700", "Dassault Falcon 2000", "Dassault Falcon 7X", "Dassault Falcon 8X",
  "Beechcraft King Air 350", "Pilatus PC-12", "Pilatus PC-24", "HondaJet Elite II", "Learjet 75",
];

export const POSITIONS = ["All positions", "PIC", "SIC", "Flight Attendant", "Maintenance"];
